import {
  getDefaultVariant,
  normalizeOptionsObj,
  calculateNewVariant
} from './product-context-helpers';
import getVariantPayload from '@/utils/getVariantPayload';

/**
 * The options a product falls back to when the URL doesn't specify any.
 */
function getDefaultOptions(productData, settings) {
  const defaultVariant = getDefaultVariant(
    productData,
    settings.overwrite_default_variant
  );

  return normalizeOptionsObj(defaultVariant.selectedOptions);
}

/**
 * Figures out which options (e.g. { size: 'Queen' }) a product should start on,
 * given its Contentstack settings and the URL's search params.
 *
 * `searchParams` is the plain object Next.js resolves the page's `searchParams`
 * promise to (e.g. { size: 'King' }).
 */
export function getOptionsFromSearchParams(
  productData,
  settings,
  searchParams
) {
  const defaultOptions = getDefaultOptions(productData, settings);
  const optionsAvailable = productData?.shopifyProduct?.options;

  if (!optionsAvailable?.length) return defaultOptions;

  // Keep only the params that name a real option on this product. A repeated
  // param (?size=King&size=Queen) arrives as an array, so take the first.
  const optionsFromUrl = {};
  for (const [key, value] of Object.entries(searchParams ?? {})) {
    if (optionsAvailable.some((o) => o.name.toLowerCase() === key)) {
      optionsFromUrl[key] = Array.isArray(value) ? value[0] : value;
    }
  }

  if (!Object.keys(optionsFromUrl).length) return defaultOptions;

  // The URL only has to specify some of the options, so fill in the rest from
  // the defaults, then confirm that combination actually exists as a variant.
  const mergedOptions = { ...defaultOptions, ...optionsFromUrl };
  const foundVariant = productData.shopifyProduct.variants.find((variant) =>
    variant.selectedOptions.every(
      (option) =>
        mergedOptions[option.name.toLowerCase()]?.toLowerCase() ===
        option.value?.toLowerCase()
    )
  );

  if (foundVariant?.selectedOptions) {
    return normalizeOptionsObj(foundVariant.selectedOptions);
  }

  // If combination is invalid, just log a warning and return null so that
  // the default options are used instead. Probably just a typo.
  console.warn(
    'Invalid option combination in URL. Available options are:',
    optionsAvailable.map((o) => o.name.toLowerCase()).join(', ')
  );

  return defaultOptions;
}

/**
 * Derives the formatted variant payload matching a set of selected options.
 */
export function computeVariant(productData, options, additionalProducts) {
  const shopifyProduct = productData?.shopifyProduct;
  if (!shopifyProduct) return undefined;

  return getVariantPayload({
    variant: calculateNewVariant(shopifyProduct, options),
    shopifyProduct,
    productData,
    additionalProducts
  });
}

/**
 * Checks that every key in an options object names a real option on the
 * product, so a bad `updateOption` call can be rejected.
 */
export function areValidOptions(shopifyProduct, options) {
  return Object.keys(options).every((key) =>
    shopifyProduct.options.some((o) => o.name.toLowerCase() === key)
  );
}
